<template>
  <!-- Expenses (Workspace369 lib/view/expenses/): the list (expenses_list/expenses_list_screen.dart,
       widgets/expense_card_widget.dart), the Add Expense chooser (expense_create_chooser.dart), the
       AI receipt scan (services/ai/expense_scan_coordinator.dart) and the form
       (manage_expense_details/manage_expense_details_screen.dart). Strings are the app's
       (app_en.arb expenses, searchExpense, addExpense, sortBy, expenseType, today, yesterday,
       scanInvoiceReceipt, enterManually, prefilledByAi, lowConfidenceDoubleCheck, details, lineItemsTitle, linkedProject,
       accounting, accountingAccount, costCenter, expenseAttachments; the scan loader text is
       hardcoded in the app). The chooser's AI subtitle is left out. Vendors, amounts and accounts
       are sample data. States:
         list      the list, before
         chooser   Add Expense opens the chooser
         scanning  the receipt is read
         prefilled the form, filled in by AI
         lines     the line items, tax and total
         linked    project, accounting account and attachment
         added     the new expense at the top of Today -->
  <StoryAppWindow title="Expenses" :icon="icons.receipt">
    <template #actions>
      <span class="saw-btn exs-add"><svg viewBox="0 0 24 24"><path :d="icons.plus" /></svg>Add Expense</span>
    </template>

    <!-- The list -->
    <section class="saw-layer exs-list" :class="{ 'is-on': isList, 'is-added': state === 'added' }">
      <div class="exs-tools">
        <span class="exs-search"><svg viewBox="0 0 24 24"><path :d="icons.search" /></svg>Search Expense</span>
        <span class="exs-chip">Sort By</span>
        <span class="exs-chip">Expense Type</span>
      </div>
      <div class="saw-card exs-rows">
        <template v-for="group in groups" :key="group.label">
          <p class="exs-date">{{ group.label }}</p>
          <div v-for="row in group.rows" :key="row.name" class="exs-row" :class="{ 'exs-row--new': row.fresh }">
            <span class="exs-tile" :style="{ '--tone': types[row.type].tone }"><svg viewBox="0 0 24 24"><path :d="types[row.type].icon" /></svg></span>
            <span class="exs-name"><b>{{ row.name }}</b><small>{{ row.type }}</small></span>
            <b class="exs-amt saw-num">{{ row.amount }}</b>
            <span class="exs-more" aria-hidden="true">⋯</span>
          </div>
        </template>
      </div>

      <div class="saw-card exs-menu" :class="{ 'is-open': state === 'chooser' }">
        <span class="exs-opt is-hot"><svg viewBox="0 0 24 24"><path :d="icons.scan" /></svg>Scan invoice/receipt</span>
        <span class="exs-opt"><svg viewBox="0 0 24 24"><path :d="icons.edit" /></svg>Enter manually</span>
      </div>
    </section>

    <!-- Reading the receipt -->
    <section class="saw-layer exs-scan" :class="{ 'is-on': state === 'scanning' }">
      <div class="exs-receipt" aria-hidden="true">
        <b>Figma</b>
        <i /><i /><i class="is-short" />
        <span><em>Total</em><em>$45.00</em></span>
        <span class="exs-beam" />
      </div>
      <p>Reading your receipt with AI…</p>
    </section>

    <!-- The form -->
    <section class="saw-layer exs-form" :class="{ 'is-on': isForm }">
      <div class="exs-form-head"><b>Add Expense</b></div>

      <div class="exs-step" :class="{ 'is-on': state === 'prefilled' }">
        <p class="exs-ai"><svg viewBox="0 0 24 24"><path :d="icons.sparkle" /></svg>Prefilled by AI — review before saving.</p>
        <div class="saw-card exs-sec">
          <b class="exs-sec-title">Details</b>
          <div class="exs-fields">
            <label class="is-wide"><small>Name</small><span>Figma</span></label>
            <label class="is-wide"><small>Description</small><span>Professional plan, 3 editors, October</span></label>
            <label><small>Amount</small><span class="saw-num">$45.00</span></label>
            <label><small>Date</small><span>Oct 21, 2026</span></label>
            <label class="is-wide"><small>Expense Type</small><span><em class="exs-type" :style="{ '--tone': types.Software.tone }">Software</em></span></label>
          </div>
          <p class="exs-unsure"><svg viewBox="0 0 24 24"><path :d="icons.unsure" /></svg>Low confidence — double-check this.</p>
        </div>
      </div>

      <div class="exs-step" :class="{ 'is-on': state === 'lines' }">
        <div class="saw-card exs-sec">
          <b class="exs-sec-title">Line items</b>
          <div class="exs-lines">
                        <div v-for="line in lines" :key="line.d" class="exs-line">
              <span>{{ line.d }}</span><span class="saw-num">{{ line.q }}</span><span class="saw-num">{{ line.p }}</span><b class="saw-num">{{ line.a }}</b>
            </div>
          </div>
          <dl class="exs-sum">
            <div><dt>Subtotal</dt><dd class="saw-num">$45.00</dd></div>
            <div><dt>Tax</dt><dd class="saw-num">$0.00</dd></div>
            <div class="is-total"><dt>Total</dt><dd class="saw-num">$45.00</dd></div>
          </dl>
        </div>
      </div>

      <div class="exs-step" :class="{ 'is-on': state === 'linked' }">
        <div class="saw-card exs-sec">
          <b class="exs-sec-title">Linked Project</b>
          <label class="exs-pick"><small>Project</small><span>Brand identity</span></label>
        </div>
        <div class="saw-card exs-sec">
          <b class="exs-sec-title">Accounting</b>
          <div class="exs-fields">
            <label><small>Accounting Account</small><span>6800 - Software &amp; Subscriptions</span></label>
            <label><small>Cost Center</small><span class="saw-muted">None</span></label>
          </div>
        </div>
        <div class="saw-card exs-sec">
          <b class="exs-sec-title">Expense Attachments</b>
          <span class="exs-file"><svg viewBox="0 0 24 24"><path :d="icons.file" /></svg>figma-receipt-oct.pdf</span>
        </div>
        <div class="exs-actions"><span class="saw-btn-quiet">Cancel</span><span class="saw-btn">Add Expense</span></div>
      </div>
    </section>
  </StoryAppWindow>
</template>

<script setup>
const props = defineProps({
  state: { type: String, default: "added" },
});

const isList = computed(() => ["list", "chooser", "added"].includes(props.state));
const isForm = computed(() => ["prefilled", "lines", "linked"].includes(props.state));

const icons = {
  receipt: "M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z",
  plus: "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z",
  search: "M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z",
  scan: "M3 5v4h2V5h4V3H5c-1.1 0-2 .9-2 2zm2 10H3v4c0 1.1.9 2 2 2h4v-2H5v-4zm14 4h-4v2h4c1.1 0 2-.9 2-2v-4h-2v4zm0-16h-4v2h4v4h2V5c0-1.1-.9-2-2-2zM7 11h10v2H7z",
  edit: "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z",
  sparkle: "M12 2l2.2 6.6L21 11l-6.8 2.4L12 20l-2.2-6.6L3 11l6.8-2.4z",
  unsure: "M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z",
  file: "M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z",
};

const types = {
  Software: { tone: "#3f58bf", icon: "M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" },
  Travel: { tone: "#0e7490", icon: "M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" },
  "Office supplies": { tone: "#a16207", icon: "M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z" },
};

const fresh = { name: "Figma", type: "Software", amount: "$45.00", fresh: true };
const groups = computed(() => [
  { label: "Today", rows: props.state === "added" ? [fresh, { name: "Stock art", type: "Software", amount: "$120.00" }] : [{ name: "Stock art", type: "Software", amount: "$120.00" }] },
  { label: "Yesterday", rows: [{ name: "Train fare", type: "Travel", amount: "$86.40" }, { name: "Printer paper", type: "Office supplies", amount: "$24.99" }] },
]);

const lines = [
  { d: "Professional plan, editor", q: "3", p: "$15.00", a: "$45.00" },
];
</script>

<style scoped>
.exs-add { padding: 5px 9px; font-size: 11.5px; }
.exs-add svg { width: 13px; height: 13px; fill: currentColor; }

.exs-list { position: relative; }
.exs-tools { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.exs-search { display: inline-flex; flex: 1 1 9rem; align-items: center; gap: 6px; border: 1px solid var(--saw-line); border-radius: 6px; background: var(--saw-card); color: var(--saw-muted); padding: 5px 9px; font-size: 12px; }
.exs-search svg { width: 14px; height: 14px; fill: currentColor; }
.exs-chip { border: 1px solid var(--saw-line); border-radius: 5px; background: var(--saw-card); padding: 4px 8px; font-size: 11.5px; white-space: nowrap; }
.exs-rows { overflow: hidden; }
.exs-date { margin: 0; background: var(--saw-wash); color: var(--saw-muted); padding: 5px 12px; font-size: 11px; font-weight: 600; }
.exs-row { display: grid; grid-template-columns: 34px minmax(0, 1fr) auto 14px; align-items: center; gap: 10px; min-height: 48px; border-top: 1px solid var(--saw-line); padding: 6px 12px; }
.exs-tile { display: grid; place-items: center; width: 34px; height: 34px; border-radius: 8px; background: color-mix(in srgb, var(--tone) 14%, transparent); color: var(--tone); }
.exs-tile svg { width: 17px; height: 17px; fill: currentColor; }
.exs-name { display: grid; min-width: 0; line-height: 1.3; }
.exs-name b { overflow: hidden; font-size: 12.5px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.exs-name small { color: var(--saw-muted); font-size: 11px; }
.exs-amt { font-size: 12.5px; }
.exs-more { color: var(--saw-muted); }
.exs-row--new { background: color-mix(in srgb, var(--saw-green-fill) 60%, transparent); }
.is-added .exs-row--new { animation: exs-in 0.5s cubic-bezier(0.33, 1, 0.68, 1) both; }
@keyframes exs-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }

.exs-menu { position: absolute; top: 0; right: 0; display: grid; width: min(15rem, 80%); padding: 5px; box-shadow: 0 12px 28px rgb(0 0 0 / 0.16); opacity: 0; transform: translateY(-4px); transition: opacity 0.25s, transform 0.3s; }
.exs-menu.is-open { opacity: 1; transform: none; }
.exs-opt { display: flex; align-items: center; gap: 9px; border-radius: 6px; padding: 9px 10px; font-size: 12.5px; font-weight: 600; }
.exs-opt svg { width: 16px; height: 16px; fill: var(--saw-muted); }
.exs-opt.is-hot { background: var(--saw-wash); }

.exs-scan { align-items: center; justify-content: center; gap: 14px; }
.exs-scan p { margin: 0; color: var(--saw-muted); font-size: 12.5px; }
.exs-receipt { position: relative; display: grid; gap: 7px; width: 9.5rem; overflow: hidden; border: 1px solid var(--saw-line); border-radius: 6px; background: var(--saw-card); padding: 14px 12px; box-shadow: 0 8px 22px rgb(0 0 0 / 0.1); }
.exs-receipt b { font-size: 13px; }
.exs-receipt i { height: 6px; border-radius: 3px; background: var(--saw-line); }
.exs-receipt i.is-short { width: 60%; }
.exs-receipt span { display: flex; justify-content: space-between; font-size: 11px; }
.exs-receipt em { font-style: normal; font-weight: 600; }
.exs-receipt .exs-beam { position: absolute; inset: 0 0 auto; display: block; height: 30%; background: linear-gradient(to bottom, transparent, rgb(14 116 144 / 0.22), transparent); transform: translateY(240%); }
.saw-layer.is-on .exs-beam { animation: exs-beam 1.4s ease-in-out 2 alternate; }
@keyframes exs-beam { from { transform: translateY(-100%); } to { transform: translateY(240%); } }

.saw-layer.exs-form { display: grid; position: relative; grid-template-rows: auto minmax(0, 1fr); align-content: start; }
.exs-form-head { font-size: 13px; }
.exs-step { display: grid; grid-area: 2 / 1; align-content: start; gap: 8px; opacity: 0; visibility: hidden; transition: opacity 0.3s, visibility 0s linear 0.3s; }
.exs-step.is-on { opacity: 1; visibility: visible; transition: opacity 0.3s; }
.exs-ai { display: flex; align-items: center; gap: 7px; margin: 0; border-radius: 6px; background: var(--saw-blue-fill); color: var(--saw-blue); padding: 8px 10px; font-size: 12px; font-weight: 600; }
.exs-unsure { display: flex; align-items: center; gap: 6px; margin: 0; color: var(--saw-muted); font-size: 11.5px; }
.exs-unsure svg { flex: none; width: 14px; height: 14px; fill: currentColor; }
.exs-ai svg { flex: none; width: 15px; height: 15px; fill: currentColor; }
.exs-sec { display: grid; gap: 9px; padding: 11px 12px; }
.exs-sec-title { font-size: 12.5px; }
.exs-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.exs-fields label, .exs-pick { display: grid; gap: 3px; min-width: 0; }
.exs-fields label.is-wide { grid-column: 1 / -1; }
.exs-fields small, .exs-pick small { color: var(--saw-muted); font-size: 10.5px; }
.exs-fields span, .exs-pick span { overflow: hidden; border: 1px solid var(--saw-line); border-radius: 6px; padding: 6px 8px; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.exs-pick i { color: var(--saw-muted); font-style: normal; }
.exs-type { border-radius: 4px; background: color-mix(in srgb, var(--tone) 14%, transparent); color: var(--tone); padding: 1px 6px; font-size: 11px; font-style: normal; font-weight: 600; }
.exs-lines { display: grid; font-size: 12px; }
.exs-lh, .exs-line { display: grid; grid-template-columns: minmax(0, 1fr) 2.4em 5.2em 5.2em; gap: 8px; padding: 6px 0; }
.exs-lh { color: var(--saw-muted); font-size: 10.5px; }
.exs-line { border-top: 1px solid var(--saw-line); }
.exs-lh span:not(:first-child), .exs-line > :not(:first-child) { text-align: right; }
.exs-sum { display: grid; gap: 4px; margin: 0; border-top: 1px solid var(--saw-line); padding-top: 8px; font-size: 12px; }
.exs-sum div { display: flex; justify-content: space-between; }
.exs-sum dt { color: var(--saw-muted); }
.exs-sum dd { margin: 0; }
.exs-sum .is-total { font-weight: 700; }
.exs-sum .is-total dt { color: inherit; }
.exs-file { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; }
.exs-file svg { width: 15px; height: 15px; fill: var(--saw-muted); }
.exs-actions { display: flex; justify-content: flex-end; gap: 6px; }

@media (max-width: 639px) {
  .exs-chip:last-child { display: none; }
  .exs-lh, .exs-line { grid-template-columns: minmax(0, 1fr) 2em 4.6em; }
  .exs-lh span:nth-child(3), .exs-line span:nth-child(3) { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .is-added .exs-row--new, .saw-layer.is-on .exs-beam { animation: none; }
  .exs-menu, .exs-step { transition: none; }
}
</style>
